import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Star, ArrowUp, ArrowDown, X } from 'lucide-react';
import { Admin, type SpeedDial } from '@/lib/api';

export default function AdminSpeedDials() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ['admin-speed-dials'], queryFn: Admin.listGlobalSpeedDials });
  const [adding, setAdding] = useState(false);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-speed-dials'] });

  const remove = useMutation({
    mutationFn: (id: number) => Admin.removeGlobalSpeedDial(id),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Partial<{ label: string; number: string; position: number }> }) =>
      Admin.updateGlobalSpeedDial(id, patch),
    onSuccess: invalidate,
  });

  const move = (entry: SpeedDial, dir: -1 | 1) => {
    update.mutate({ id: entry.id, patch: { position: (entry.position ?? 0) + dir } });
  };

  return (
    <div className="p-5 max-w-4xl mx-auto">
      <header className="flex items-center justify-between mb-3">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Star className="text-amber-500" size={22} /> Kısa Yollar & Yöneticiler
          </h1>
          <p className="text-sm text-slate-500">
            Tüm agent'ların softphone'unda görünür. Yöneticiler, supervisor'lar, ortak servis numaraları için.
          </p>
        </div>
        <button
          onClick={() => setAdding(true)}
          className="flex items-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
        >
          <Plus size={16} /> Yeni
        </button>
      </header>

      <div className="bg-surface rounded-lg border border-slate-200 overflow-hidden">
        {list.isLoading && <div className="p-6 text-slate-500">Yükleniyor…</div>}
        {list.data && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-2 w-20">Sıra</th>
                <th className="text-left px-4 py-2">Etiket</th>
                <th className="text-left px-4 py-2">Numara</th>
                <th className="text-right px-4 py-2 w-32">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((sd, i) => (
                <tr key={sd.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono text-slate-500">
                    <div className="flex items-center gap-1">
                      {sd.position ?? 0}
                      <button onClick={() => move(sd, -1)} disabled={i === 0} className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowUp size={12} /></button>
                      <button onClick={() => move(sd, 1)} disabled={i === list.data.length - 1} className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-30"><ArrowDown size={12} /></button>
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <InlineText
                      value={sd.label}
                      onSave={(v) => update.mutate({ id: sd.id, patch: { label: v } })}
                    />
                  </td>
                  <td className="px-4 py-2 font-mono">
                    <InlineText
                      value={sd.number}
                      onSave={(v) => update.mutate({ id: sd.id, patch: { number: v } })}
                    />
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => { if (confirm(`'${sd.label}' silinsin mi?`)) remove.mutate(sd.id); }}
                      className="text-slate-500 hover:text-red-600 p-1"
                    ><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
              {list.data.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-400">Henüz hiç kayıt yok.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {adding && <AddModal onClose={() => setAdding(false)} />}
    </div>
  );
}

function InlineText({ value, onSave }: { value: string; onSave: (v: string) => void }) {
  const [v, setV] = useState(value);
  return (
    <input
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (v !== value && v.trim()) onSave(v.trim()); }}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      className="bg-transparent px-1 py-0.5 hover:bg-surface border border-transparent hover:border-slate-300 rounded w-full focus:bg-surface focus:border-blue-400 focus:outline-none"
    />
  );
}

function AddModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [label, setLabel] = useState('');
  const [number, setNumber] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => Admin.createGlobalSpeedDial({ label: label.trim(), number: number.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-speed-dials'] });
      onClose();
    },
    onError: (e: any) => setErr(e?.response?.data?.message ?? e.message),
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-surface rounded-lg shadow-xl w-[400px]">
        <header className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="font-semibold">Yeni Kısa Yol</h2>
          <button onClick={onClose} className="text-slate-500"><X size={18} /></button>
        </header>
        <div className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-medium mb-1">Etiket (örn: "Operasyon Müdürü")</label>
            <input
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full px-2 py-1.5 border rounded"
            />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Numara (extension veya dış numara)</label>
            <input
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              className="w-full px-2 py-1.5 border rounded font-mono"
              placeholder="1010 veya 905..."
            />
          </div>
          {err && <div className="text-sm text-red-600">{err}</div>}
        </div>
        <footer className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-sm hover:bg-slate-100 rounded">İptal</button>
          <button
            onClick={() => create.mutate()}
            disabled={!label.trim() || !number.trim() || create.isPending}
            className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white text-sm rounded"
          >Kaydet</button>
        </footer>
      </div>
    </div>
  );
}
